import { EXPORT_FILE_STATUS, formatParisDateTime } from '@flux/shared';
import { Download } from 'lucide-react';
import type { JSX } from 'react';
import { StatusBadge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { EmptyState } from '@/components/ui/empty-state.js';
import { ErrorState } from '@/components/ui/error-state.js';
import { Skeleton } from '@/components/ui/skeleton.js';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table.js';
import { ApiError, downloadFromApi } from '@/lib/api/client.js';
import { useOrderFiles } from '../use-orders-queries.js';

/**
 * Versions du CSV (`02` §4, onglet « Aperçu CSV ») : tous les fichiers générés
 * pour la commande, renvois compris. Pas de renvoi fichier par fichier : on
 * renvoie la commande (« Renvoyer la commande à Martelet »).
 */
export function FilesTab({ orderId }: { orderId: string }): JSX.Element {
  const filesQuery = useOrderFiles(orderId);

  if (filesQuery.isPending) {
    return (
      <div className="flex flex-col gap-3 rounded-lg border border-border bg-card p-4">
        {Array.from({ length: 3 }, (_, index) => (
          <Skeleton key={`files-skeleton-${String(index)}`} className="h-3.5 w-full" />
        ))}
      </div>
    );
  }

  if (filesQuery.isError) {
    return (
      <ErrorState
        message={
          filesQuery.error instanceof ApiError
            ? filesQuery.error.message
            : 'Impossible de charger les fichiers.'
        }
        detail={filesQuery.error instanceof ApiError ? filesQuery.error.detail : undefined}
        onRetry={() => void filesQuery.refetch()}
      />
    );
  }

  const files = filesQuery.data ?? [];

  if (files.length === 0) {
    return (
      <EmptyState
        bordered
        title="Aucun fichier"
        description="Aucun CSV n'a encore été généré pour cette commande."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Nom</TableHead>
            <TableHead>Statut</TableHead>
            <TableHead>Généré le</TableHead>
            <TableHead>Envoyé le</TableHead>
            <TableHead className="text-right">Tentatives</TableHead>
            <TableHead>Dernière erreur</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {files.map((file) => (
            <TableRow key={file.id}>
              <TableCell className="whitespace-nowrap font-medium">{file.name}</TableCell>
              <TableCell>
                <StatusBadge registry={EXPORT_FILE_STATUS} code={file.status} />
              </TableCell>
              <TableCell className="whitespace-nowrap tabular-nums">
                {formatParisDateTime(new Date(file.createdAt))}
              </TableCell>
              <TableCell className="whitespace-nowrap tabular-nums">
                {file.sentAt ? formatParisDateTime(new Date(file.sentAt)) : '—'}
              </TableCell>
              <TableCell className="text-right tabular-nums">{file.attempts}</TableCell>
              <TableCell
                className={
                  file.lastError ? 'max-w-64 truncate text-status-red-fg' : 'text-muted-foreground'
                }
                title={file.lastError ?? undefined}
              >
                {file.lastError ?? '—'}
              </TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={!file.hasContent}
                    onClick={() => {
                      downloadFromApi(`/api/files/${file.id}/download`);
                    }}
                  >
                    <Download aria-hidden="true" />
                    Télécharger
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
